import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import '../../styles/_api-docs.scss';
import { Box } from '@availity/element';
import { IconButton } from '@mui/material';
import { Menu as MenuIcon } from '@mui/icons-material';
import useApiSpec from '../../hooks/useApiSpec';
import useThemeStore from '../../hooks/useThemeStore';
import { useSessionStore } from '../../hooks';
import type { SessionStore } from '../../auth/sessionService';
import GuidesTab from './GuidesTab';
import ApiReferenceTab from './ApiReferenceTab';
import AIManagementTab from './AIManagementTab';
import AccessHubTab from './AccessHubTab';
import SidebarSkeleton from './SidebarSkeleton';

const getActiveTab = (pathname: string) => {
  if (['/docs/api-spec', '/tag/', '/operation/', '/model/'].some((p) => pathname.startsWith(p))) return 'api-reference';
  if (pathname.startsWith('/docs/ai-management')) return 'ai-management';
  if (pathname.startsWith('/access-hub')) return 'access-hub';
  return 'guides';
};

const ApiDocs = (): JSX.Element => {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, setTheme } = useThemeStore();
  const toggleTheme = () => setTheme(theme === 'dark' ? 'light' : 'dark');
  const isAuthenticated = useSessionStore((store: unknown) => (store as SessionStore).isAuthenticated);
  const { apiSpec, loading, error, refetch } = useApiSpec();
  const [mcpSidebarOpen, setMcpSidebarOpen] = useState(false);
  const [guidesSidebarOpen, setGuidesSidebarOpen] = useState(false);

  const activeTab = getActiveTab(location.pathname);

  const colors =
    theme === 'light'
      ? {
          bg: '#FFFFFF',
          bgSecondary: '#EFF3FF',
        }
      : {
          bg: '#151823',
          bgSecondary: '#191F31',
        };

  return (
    <div className="api-docs-container">
      <div className={`docs-tabs ${theme === 'light' ? 'light' : ''}`}>
        <div className="tabs-left">
          <button
            type="button"
            className=""
            onClick={() => navigate('/')}
          >
            Home
          </button>
          <button
            type="button"
            className={activeTab === 'guides' ? 'active' : ''}
            onClick={() => navigate('/docs/overview')}
          >
            Overview
          </button>
          <button
            type="button"
            className={activeTab === 'api-reference' ? 'active' : ''}
            onClick={() => navigate('/docs/api-spec')}
          >
            API Specification
          </button>
          <button
            type="button"
            className={activeTab === 'ai-management' ? 'active' : ''}
            onClick={() => navigate('/docs/ai-management')}
          >
            Model Context Protocol (MCP)
          </button>
          {isAuthenticated && (
            <button
              type="button"
              className={activeTab === 'access-hub' ? 'active' : ''}
              onClick={() => navigate('/access-hub')}
            >
              Access Hub
            </button>
          )}
        </div>
        <div className="themes-section desktop-only">
          <label className="switch" aria-label="Toggle theme">
            <input
              type="checkbox"
              checked={theme === 'dark'}
              onChange={toggleTheme}
            />
            <span className="slider round" />
          </label>
        </div>
      </div>
      {activeTab !== 'api-reference' && (
      <div className={`docs-mobile-controls ${theme === 'light' ? 'light' : ''}`}>
        {(activeTab === 'ai-management' || activeTab === 'guides') && (
          <IconButton
            onClick={() => activeTab === 'ai-management' ? setMcpSidebarOpen(true) : setGuidesSidebarOpen(true)}
            aria-label="Open sidebar"
            sx={{ color: theme === 'light' ? '#0D2543' : '#fff', p: '4px' }}
          >
            <MenuIcon />
          </IconButton>
        )}
        <div className="themes-section">
          <label className="switch" aria-label="Toggle theme">
            <input
              type="checkbox"
              checked={theme === 'dark'}
              onChange={toggleTheme}
            />
            <span className="slider round" />
          </label>
        </div>
      </div>
      )}
      {activeTab === 'guides' ? (
        <GuidesTab theme={theme} sidebarOpen={guidesSidebarOpen} onSidebarClose={() => setGuidesSidebarOpen(false)} />
      ) : activeTab === 'api-reference' ? (
        loading ? (
           <Box
            className="api-docs-loading"
            sx={{
              display: 'flex',
              minHeight: 'calc(100vh - 120px)',
              bgcolor: colors.bg,
              position: 'relative',
              flexDirection: { xs: 'column', md: 'row' },
              justifyContent: 'center',
            }}
          >
            <SidebarSkeleton theme={theme} />
            <Box
              className="api-docs-loading__container"
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: '32px',
                flex: 1,
                justifyContent: 'center',
              }}
            >
              <Box
                className="api-docs-loading__content"
                sx={{
                  flex: 1,
                  p: { xs: '24px 16px', sm: '40px 24px', md: '64px 32px', lg: '64px 64px' },
                  bgcolor: colors.bg,
                  maxWidth: { xs: '100%', sm: '688px', md: '80%' },
                  mx: { xs: 'auto', sm: 'auto', md: 0 },
                  width: '100%',
                }}
              >
                <Box className="api-docs-loading__line api-docs-loading__line--header" sx={{ height: 40, bgcolor: colors.bgSecondary, mb: 4, borderRadius: '4px', width: '60%' }} />
                <Box className="api-docs-loading__line" sx={{ height: 20, bgcolor: colors.bgSecondary, mb: 2, borderRadius: '4px' }} />
                <Box className="api-docs-loading__line" sx={{ height: 20, bgcolor: colors.bgSecondary, mb: 2, borderRadius: '4px' }} />
                <Box className="api-docs-loading__line api-docs-loading__line--short" sx={{ height: 20, bgcolor: colors.bgSecondary, mb: 2, borderRadius: '4px', width: '80%' }} />
              </Box>
            </Box>
          </Box>
        ) : error ? (
          <div className="error-message">
            <p>Error loading API documentation: {error}</p>
            <button type="button" onClick={refetch}>
              Retry
            </button>
          </div>
        ) : (
           apiSpec && <ApiReferenceTab apiSpec={apiSpec} theme={theme} onToggleTheme={toggleTheme} />
        )
      ) : activeTab === 'access-hub' ? (
        <AccessHubTab theme={theme} />
      ) : (
        <AIManagementTab theme={theme} sidebarOpen={mcpSidebarOpen} onSidebarClose={() => setMcpSidebarOpen(false)} />
      )}
    </div>
  );
};

export default ApiDocs;
